import type { ComponentProps } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { cn } from '@poseidon/components/cn';
import { Kbd } from '@poseidon/components/Kbd';
import {
  Charts,
  Communication,
  Development,
  Files,
  General,
  Layout,
  Maps,
  Users,
} from '@poseidon/components/StrokeIcons';

import { LeftNavRow } from '~/components/LeftNav/LeftNavRow';
import { LogoRow } from '~/components/LeftNav/LogoRow';
import { UserLogoutRow } from '~/components/UserLogoutRow';

export type LeftNavUserTeam = ComponentProps<typeof UserLogoutRow>['authData'] & {
  teamId: string;
  userId: string;
  hubspotConnected?: string | null;
  /** Gates the "Switches" nav entry (campaign-switch feature). */
  campaignSwitchEnabled?: boolean;
  /** Gates the "Sequence" nav entry (graph-based journey engine). */
  sequenceEnabled?: boolean;
  /** Gates the "Bad Numbers" nav entry (phone re-enrichment waterfall feature). */
  phoneReEnrichmentEnabled?: boolean;
};

export type LeftNavPureProps = {
  userTeam: LeftNavUserTeam;
  navMinimized: boolean;
  setNavMinimized: (newValue: boolean | null) => void;
  /** Current route pathname — forwarded to each row so active state comes from props, not the router. */
  pathname: string;
  isMac: boolean;
  isImpersonating: boolean;
  takeActionCount: number;
  repliesCount: number;
  hubspotProblemCount: number | undefined;
  /** Per-badge shake flags, owned by the container so `NotificationLabel` stays pure. */
  takeActionShaking: boolean;
  repliesShaking: boolean;
  hubspotShaking: boolean;
  /** Sublink expand/collapse state, owned by the container so the rows stay pure. */
  openRows: Record<string, boolean>;
  onRowOpenChange: (key: string, open: boolean) => void;
};

/**
 * Pure presentational left nav. Every subcomponent it renders (`LogoRow`,
 * `LeftNavRow`, `NotificationLabel`, `UserLogoutRow`) is a pure function of its
 * props too — all data fetching, router, localStorage, auth context, keyboard
 * listeners, and local UI state (nav badge shake, sublink open/close) live in
 * the `LeftNav` container and arrive here as props. Render it directly with
 * fixture props in tests/Storybook; no tRPC/router/auth environment required.
 *
 * (The only stateful code left in the subtree lives inside shared design-system
 * leaves — Input2 `Button`/`Tooltip`, `Avatar` — and framer-motion, none of
 * which read app data, the router, or globals.)
 */
export const LeftNavPure = ({
  userTeam,
  navMinimized,
  setNavMinimized,
  pathname,
  isMac,
  isImpersonating,
  takeActionCount,
  repliesCount,
  hubspotProblemCount,
  takeActionShaking,
  repliesShaking,
  hubspotShaking,
  openRows,
  onRowOpenChange,
}: LeftNavPureProps) => {
  return (
    <AnimatePresence>
      <motion.div
        data-minimized={navMinimized}
        className={cn(
          'absolute inset-y-0 left-0 flex h-full w-(--nav-width) flex-col items-stretch border-r border-(--nav-border-color) bg-(--nav-bg-color) font-sans text-(--nav-txt-color) transition-[width] duration-200',
        )}
        transition={{ type: 'spring' }}>
        <LogoRow navMinimized={navMinimized} setNavMinimized={setNavMinimized} isMac={isMac} />

        <div
          data-intro="Things!"
          className={cn(
            'flex grow basis-0 flex-col items-stretch gap-1 overflow-auto px-4 transition-[padding] duration-300 ease-[cubic-bezier(0.05,0.61,0.41,0.95)] hide-scrollbar',
            navMinimized && 'px-[calc((var(--nav-width)-24px-16px)/2)]',
          )}>
          <LeftNavRow
            href="/todo"
            icon={<General.Zap />}
            minimized={navMinimized}
            pathname={pathname}
            counterValue={takeActionCount > 0 ? takeActionCount : undefined}
            gradientBadge={takeActionCount > 0}
            gradientActive={takeActionCount > 0}
            isShaking={takeActionShaking}>
            Take Action
          </LeftNavRow>
          {isImpersonating && (
            <LeftNavRow
              href={{ pathname: '/leads/responses', query: { userId: 'team' } }}
              icon={<Communication.MessageCheckSquare />}
              minimized={navMinimized}
              pathname={pathname}
              counterValue={repliesCount > 0 ? repliesCount : undefined}
              gradientBadge={repliesCount > 0}
              gradientActive={repliesCount > 0}
              isShaking={repliesShaking}>
              Replies
            </LeftNavRow>
          )}
          <LeftNavRow
            href="/templates"
            icon={<Maps.Rocket />}
            minimized={navMinimized}
            pathname={pathname}
            addLink="/templates/add"
            addLinkLabel="Add Campaign">
            Campaigns
          </LeftNavRow>
          {userTeam.campaignSwitchEnabled && (
            <LeftNavRow href="/switches" icon={<Development.Dataflow03 />} minimized={navMinimized} pathname={pathname}>
              Switches
            </LeftNavRow>
          )}
          {userTeam.sequenceEnabled && (
            <LeftNavRow href="/sequence" icon={<Development.Dataflow04 />} minimized={navMinimized} pathname={pathname}>
              Sequence
            </LeftNavRow>
          )}
          <LeftNavRow
            href="/prompts"
            icon={<Communication.MessageTextSquare02 />}
            minimized={navMinimized}
            pathname={pathname}
            addLink="/prompts/add"
            addLinkLabel="Add Message">
            Templates
          </LeftNavRow>

          <LeftNavRow href="/prospects" icon={<Users.Users01 />} minimized={navMinimized} pathname={pathname}>
            Search <Kbd>{isMac ? '⌘' : 'ctrl'}+K</Kbd>
          </LeftNavRow>
          <LeftNavRow href="/pipeline" icon={<Layout.AlignTopArrow02 />} minimized={navMinimized} pathname={pathname}>
            Pipeline
          </LeftNavRow>

          <LeftNavRow
            href="/results"
            icon={<Users.UsersPlus />}
            minimized={navMinimized}
            pathname={pathname}
            isOpen={openRows['/results'] ?? false}
            onOpenChange={(o) => onRowOpenChange('/results', o)}
            subLinks={[
              {
                href: '/activities',
                icon: <Charts.BarChartSquare02 />,
                children: 'Analytics',
              },
            ]}>
            Connection results
          </LeftNavRow>
          <LeftNavRow
            href="/content"
            icon={<Files.FileAttachment02 />}
            addLink="/content?add=true"
            addLinkLabel="Add Content"
            minimized={navMinimized}
            pathname={pathname}>
            Content Library
          </LeftNavRow>

          {userTeam.hubspotConnected != null && userTeam.hubspotConnected != 'off' && (
            <LeftNavRow
              href="/hubspot/unmatched"
              counterValue={hubspotProblemCount}
              icon={<Development.GitMerge />}
              minimized={navMinimized}
              pathname={pathname}
              isShaking={hubspotShaking}>
              CRM Sync
            </LeftNavRow>
          )}
          <div className="min-h-10" />
          <LeftNavRow href="/uploadcsv" icon={<General.UploadCloud02 />} minimized={navMinimized} pathname={pathname}>
            Upload
          </LeftNavRow>
          <LeftNavRow
            href="/emailsSent"
            icon={<Communication.Mail01 />}
            minimized={navMinimized}
            pathname={pathname}>
            Emails Sent
          </LeftNavRow>
          {userTeam.phoneReEnrichmentEnabled && (
            <LeftNavRow
              href="/bad-numbers"
              icon={<Communication.PhoneHangUp02 />}
              minimized={navMinimized}
              pathname={pathname}>
              Bad Numbers
            </LeftNavRow>
          )}
        </div>
        <div className="mx-4 mb-8 shrink-0 border-t border-(--nav-border-color) bg-(--nav-bg-color)">
          <UserLogoutRow authData={userTeam} minimized={navMinimized} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
